<template>
  <div
    class="cart__product-recommendation-items pb-sm"
    v-if="loading || cartProductRecommendation?.length"
  >
    <h2 class="text--center cart__product-recommendation-title mt-0">
      You may also like
    </h2>
    <ProductListSlider
      @item-click="closeCartPopup"
      :key="cartProductRecommendation"
      :products="
        cartProductRecommendation?.length && !loading
          ? cartProductRecommendation
          : placeholderProducts
      "
      :showFirstMockupImage="showFirstMockupImage"
    />
  </div>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, computed, ref, watch } from 'vue'
import useProductStore from '@/store/product'
import ProductListSlider from '@/components/product/ProductListSlider.vue'
import LocationService, { UserLocation } from '@/services/location'
import { Recommendation } from '@/types'
import {
  EVENT_SHOW_CART_POPUP,
  EVENT_CLOSE_CART_POPUP,
  QUERY_STRING_NO_CACHE,
} from '@/utils'
import EventBus from '@/services/eventbus'
import useUserStore from '@/store/user'
import { useRoute } from 'vue-router'
import useCartStore from '@/store/cart'
import { useProductRecentlyViewedItems } from '@/pages/product/composables/ProductRecentlyViewed'
import useDeviceStore from '@/store/device'

const deviceStore = useDeviceStore()
const route = useRoute()
const userStore = useUserStore()
const productStore = useProductStore()
const cartStore = useCartStore()
const { getRecentlyViewedItems } = useProductRecentlyViewedItems()

const loading = ref(false)
const placeholderProducts = Array(10).fill(undefined)

const isCartPage = computed(() => route.name == 'cart')

const showFirstMockupImage = computed(
  () => deviceStore.isMobile || !isCartPage.value
)

const cartProductRecommendation = computed(() => {
  return productStore.productRecommendation?.moreItems?.length
    ? productStore.productRecommendation?.moreItems
    : productStore.productRecommendationShopify
})

onMounted(async () => {
  EventBus.on(EVENT_SHOW_CART_POPUP, getProductRecommendation)

  if (route.path == '/cart') {
    watch(
      () => cartStore.cart?.id,
      () => {
        if (cartStore.cart?.lines?.length) getProductRecommendation()
      },
      {
        immediate: true,
      }
    )
  }
})

onUnmounted(() => {
  EventBus.off(EVENT_SHOW_CART_POPUP, getProductRecommendation)
  if (route.path == '/cart') {
    watch(
      () => cartStore.cart?.id,
      () => {
        if (cartStore.cart?.lines?.length) getProductRecommendation()
      },
      {
        immediate: true,
      }
    )
  }
})

function getProductRecommendation() {
  if (
    productStore.productRecommendation?.moreItems?.length ||
    productStore.productRecommendationShopify?.length
  ) {
    return
  }
  loading.value = true
  LocationService.onLocationReady(async (location: UserLocation | null) => {
    const countryCode = location?.country_code
    const recentlyViewedProducts = await getRecentlyViewedItems()

    let product = productStore.product

    if (!product) {
      product = cartStore.cart?.lines[0]?.merchandise?.product
    }
    if (!product && recentlyViewedProducts?.length) {
      product = recentlyViewedProducts[0]
    }
    if (!product) return (loading.value = false)

    const recomendationPayload: Recommendation = {
      recently_view: recentlyViewedProducts.map((product) => product.handle),
      product: {
        title: product.title,
        handle: product.handle,
        tags: product.tags || [],
        type: product.productType || '',
      },
      location: countryCode,
      user_segments: userStore.segments
        ? userStore.segments.map((segment) => segment.key)
        : [],
    }
    await productStore.getProductRecommendation(recomendationPayload)

    if (!productStore.productRecommendation?.moreItems?.length && product.id) {
      const isForceLoadNewData = !!route.query[QUERY_STRING_NO_CACHE]

      await productStore.getProductRecommendationsShopify({
        id: product.id,
        isForceLoadNewData,
      })
    }
    loading.value = false
  })
}

function closeCartPopup() {
  EventBus.trigger(EVENT_CLOSE_CART_POPUP)
}
</script>

<style lang="scss">
.cart__product-recommendation-items {
  border-radius: 5px;
  box-shadow: 0 0 3px 0 rgb(0 0 0 / 20%);

  grid-area: c;
  .product-list-slider {
    margin: 8px;
  }
  .cart__product-recommendation-title {
    font-size: 15px;
    font-weight: 500;
    opacity: 0.85;
    align-items: center;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 45px;
    background: var(--background-color);
  }
  .product-block__title a {
    -webkit-line-clamp: 2;
    font-size: 13px;
    opacity: 0.85;
  }
  @include media-lg-down {
    .product-block__image.image-container {
      padding-top: 0;
    }
    .product-block__image.image-container,
    .product-block .image-container,
    .product-block__image--rectangular img,
    .product-block .image-rectangular {
      aspect-ratio: 1;
      height: 100px !important;
    }
    .product-block__personalize-badge {
      display: none;
    }
    .product {
      &-block {
        gap: 1em;
        display: flex;
        flex-direction: row;
        &__image,
        &__image a {
          width: 100px;
        }
        &__info {
          gap: 1em;
        }
        &__price {
          font-weight: 400;
          font-size: 14px;
        }
        &__price-final {
          color: var(--color-secondary);
        }
        &__title {
          margin: 0;
        }
        &__type-and-price {
          margin: 0;
        }
      }
    }
    .carousel {
      &__button.is-prev,
      &__button.is-next {
        background: #ffffff;
        color: var(--text-color-primary);
        bottom: 0 !important;
        top: unset;
        position: absolute;
        transform: translateY(35px);
        z-index: 1;
        svg {
          width: 1rem;
        }
      }
      &__button.is-prev {
        left: 50px;
      }
      &__button.is-next {
        right: 50px;
      }
      &__dots {
        margin-top: 4px;
      }
      --carousel-slide-width: calc((100% - 1em) - 1px);
    }
  }
}

.cart-popup {
  .cart__product-recommendation-items {
    .product-block__image.image-container {
      padding-top: 0;
    }
    .product-block__image.image-container,
    .product-block .image-container,
    .product-block__image--rectangular img,
    .product-block .image-rectangular {
      aspect-ratio: 1;
      height: 100px !important;
    }
    .product-block__personalize-badge {
      display: none;
    }
    .product {
      &-block {
        gap: 1em;
        display: flex;
        flex-direction: row;
        &__image,
        &__image a {
          width: 100px;
        }
        &__info {
          gap: 1em;
        }
        &__price {
          font-weight: 400;
          font-size: 14px;
        }
        &__price-final {
          color: var(--color-secondary);
        }
        &__title {
          margin: 0;
        }
        &__type-and-price {
          margin: 0;
        }
      }
    }
    .carousel {
      &__button.is-prev,
      &__button.is-next {
        background: #ffffff;
        color: var(--text-color-primary);
        bottom: 0 !important;
        top: unset;
        position: absolute;
        transform: translateY(35px);
        z-index: 1;
        svg {
          width: 1rem;
        }
      }
      &__button.is-prev {
        left: 50px;
      }
      &__button.is-next {
        right: 50px;
      }
      &__dots {
        margin-top: 4px;
      }
      --carousel-slide-width: calc((100% - 1em) - 1px);
    }
  }
}
</style>
